import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, ImageOff, X } from 'lucide-react';

/**
 * Full-screen viewer for a single media item, with keyboard navigation.
 * Videos are rendered with native controls and never autoplay.
 */
const SchoolMediaLightbox = ({ items, activeIndex, schoolName, onClose, onNavigate }) => {
  const [hasFailed, setHasFailed] = useState(false);
  const closeButtonRef = useRef(null);

  const total = items.length;
  const item = items[activeIndex];
  const canNavigate = total > 1;

  const goTo = useCallback(
    (nextIndex) => {
      onNavigate((nextIndex + total) % total);
    },
    [onNavigate, total]
  );

  // Reset the error state whenever a different item is shown.
  useEffect(() => {
    setHasFailed(false);
  }, [activeIndex]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    if (closeButtonRef.current) closeButtonRef.current.focus();

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (!canNavigate) return;

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        goTo(activeIndex + 1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goTo(activeIndex - 1);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [activeIndex, canNavigate, goTo, onClose]);

  if (!item) return null;

  return (
    <div
      className="sp-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={`${schoolName} media viewer, item ${activeIndex + 1} of ${total}`}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="sp-lightbox-bar">
        <p className="sp-lightbox-counter" aria-live="polite">
          {activeIndex + 1} / {total}
        </p>
        <button
          type="button"
          ref={closeButtonRef}
          className="sp-lightbox-control sp-lightbox-close"
          onClick={onClose}
          aria-label="Close media viewer"
        >
          <X size={20} strokeWidth={1.5} aria-hidden="true" />
        </button>
      </div>

      <div className="sp-lightbox-stage">
        {canNavigate && (
          <button
            type="button"
            className="sp-lightbox-control sp-lightbox-nav"
            onClick={() => goTo(activeIndex - 1)}
            aria-label="Previous media item"
          >
            <ChevronLeft size={22} strokeWidth={1.5} aria-hidden="true" />
          </button>
        )}

        <figure className="sp-lightbox-figure">
          {hasFailed ? (
            <div className="sp-lightbox-fallback">
              <ImageOff size={28} strokeWidth={1.5} aria-hidden="true" />
              <p>This media could not be loaded.</p>
            </div>
          ) : item.isVideo ? (
            // eslint-disable-next-line jsx-a11y/media-has-caption
            <video
              key={item.id}
              className="sp-lightbox-media"
              src={item.mediaUrl}
              poster={item.thumbnailUrl || undefined}
              controls
              playsInline
              preload="metadata"
              aria-label={item.alt}
              onError={() => setHasFailed(true)}
            />
          ) : (
            <img
              key={item.id}
              className="sp-lightbox-media"
              src={item.mediaUrl}
              alt={item.alt}
              decoding="async"
              onError={() => setHasFailed(true)}
            />
          )}
          <figcaption className="sp-lightbox-caption">{item.alt}</figcaption>
        </figure>

        {canNavigate && (
          <button
            type="button"
            className="sp-lightbox-control sp-lightbox-nav"
            onClick={() => goTo(activeIndex + 1)}
            aria-label="Next media item"
          >
            <ChevronRight size={22} strokeWidth={1.5} aria-hidden="true" />
          </button>
        )}
      </div>

      <style>{`
        /*
         * The viewer is a deliberately dark room — a consistent, self-contained
         * surface for looking at photographs, not a stray dark section.
         */
        .sp-lightbox {
          position: fixed;
          inset: 0;
          z-index: var(--sp-z-lightbox, 50);
          background: #101114;
          display: flex;
          flex-direction: column;
          padding: 1.25rem;
          font-family: var(--sp-text);
        }

        .sp-lightbox-bar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          flex: 0 0 auto;
          padding-bottom: 1rem;
          border-bottom: 1px solid rgba(255, 255, 255, 0.14);
        }

        .sp-lightbox-counter {
          margin: 0;
          font-size: 0.75rem;
          font-weight: 600;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          font-variant-numeric: tabular-nums;
          color: rgba(255, 255, 255, 0.65);
        }

        .sp-lightbox-control {
          width: 44px;
          height: 44px;
          border-radius: 2px;
          border: 1px solid rgba(255, 255, 255, 0.22);
          background: transparent;
          color: #ffffff;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          flex: 0 0 auto;
          transition: background 0.2s ease, border-color 0.2s ease;
        }

        .sp-lightbox-control:hover {
          background: rgba(255, 255, 255, 0.1);
          border-color: rgba(255, 255, 255, 0.5);
        }

        .sp-lightbox-control:focus-visible {
          outline: 2px solid #ffffff;
          outline-offset: 3px;
        }

        .sp-lightbox-stage {
          flex: 1 1 auto;
          min-height: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 1.25rem;
        }

        .sp-lightbox-figure {
          margin: 0;
          flex: 1 1 auto;
          min-width: 0;
          height: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 1rem;
        }

        .sp-lightbox-media {
          max-width: 100%;
          max-height: calc(100dvh - 12rem);
          width: auto;
          height: auto;
          background: #000000;
          object-fit: contain;
        }

        .sp-lightbox-caption {
          font-size: 0.85rem;
          color: rgba(255, 255, 255, 0.6);
          text-align: center;
        }

        .sp-lightbox-fallback {
          color: rgba(255, 255, 255, 0.75);
          text-align: center;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.75rem;
          padding: 3rem 1.5rem;
          border: 1px solid rgba(255, 255, 255, 0.22);
        }

        .sp-lightbox-fallback p {
          margin: 0;
        }

        @media (max-width: 700px) {
          .sp-lightbox-stage {
            gap: 0.35rem;
          }

          .sp-lightbox-nav {
            position: absolute;
            bottom: 1.5rem;
          }

          .sp-lightbox-nav:first-of-type {
            left: 1.25rem;
          }

          .sp-lightbox-nav:last-of-type {
            right: 1.25rem;
          }

          .sp-lightbox-media {
            max-height: calc(100dvh - 15rem);
          }
        }
      `}</style>
    </div>
  );
};

export default SchoolMediaLightbox;
